import { useInfiniteQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Constants from '@/constants';

import { search, searchQueryKey } from '@/_generated/api-clients/xp';
import { userInAISearchPOCAtom } from '@/atoms/ai-search-poc';
import { userPrefsAtom } from '@/atoms/userPrefs';

/** Common explore search query. */
const useExploreSearch = (query: string) => {
  const { country: selectedCountry, language: languageCode } =
    useAtomValue(userPrefsAtom);

  const userInAISearchPOC = useAtomValue(userInAISearchPOCAtom);
  const { aiSearchPocQueryParameters } = useFlags();

  const maxResults = Constants.MAX_SEARCH_RESULTS;

  const smartSearchActive = userInAISearchPOC && query.split(' ').length > 2;

  const baseSearchParams = {
    country: selectedCountry.id ?? '',
    language: languageCode,
    query,
    ...(smartSearchActive ? aiSearchPocQueryParameters : {}),
  };

  return useInfiniteQuery({
    queryKey: searchQueryKey({ query: baseSearchParams }),
    queryFn: async ({ pageParam }) => {
      const result = await search({
        query: {
          ...baseSearchParams,
          pageNumber: pageParam,
          ...(smartSearchActive && pageParam > 1
            ? { 'smart.enabled': false }
            : {}),
        },
      });
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return;
      const cappedTotal = Math.min(lastPage.totalResults, maxResults);
      const loaded = allPages.reduce(
        (sum, page) => sum + (page?.results?.length ?? 0),
        0,
      );

      if (!cappedTotal || loaded >= cappedTotal) return;
      return lastPage.page && loaded < cappedTotal
        ? lastPage.page + 1
        : allPages.length + 1;
    },
    enabled: !!query && !!aiSearchPocQueryParameters,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export default useExploreSearch;
